import { Check, ChevronDown, X } from "lucide-react";
import {
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";

type SelectValue = number | string;

export type OverlaySelectOption<T extends SelectValue> = {
  value: T;
  label: string;
  disabled?: boolean;
};

export type OverlaySelectPresentation = "popover" | "sheet";

export function getOverlaySelectPresentation(
  width: number,
  height: number,
): OverlaySelectPresentation {
  return width < 600 && height >= width ? "sheet" : "popover";
}

function currentPresentation(): OverlaySelectPresentation {
  if (typeof window === "undefined") return "popover";
  return getOverlaySelectPresentation(window.innerWidth, window.innerHeight);
}

function useOverlaySelectPresentation() {
  const [presentation, setPresentation] = useState(currentPresentation);

  useEffect(() => {
    const update = () => setPresentation(currentPresentation());
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    window.addEventListener("mt:deviceorientation", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
      window.removeEventListener("mt:deviceorientation", update);
    };
  }, []);

  return presentation;
}

function enabledIndex<T extends SelectValue>(
  options: readonly OverlaySelectOption<T>[],
  start: number,
  direction: 1 | -1,
) {
  for (let offset = 1; offset <= options.length; offset += 1) {
    const index =
      (start + direction * offset + options.length) % options.length;
    if (!options[index]?.disabled) return index;
  }
  return start;
}

function edgeEnabledIndex<T extends SelectValue>(
  options: readonly OverlaySelectOption<T>[],
  edge: "first" | "last",
) {
  const indexes = options.map((_, index) => index);
  if (edge === "last") indexes.reverse();
  return indexes.find((index) => !options[index]?.disabled) ?? 0;
}

export function OverlaySelect<T extends SelectValue>({
  ariaLabel,
  className = "",
  disabled = false,
  onChange,
  options,
  value,
}: {
  ariaLabel: string;
  className?: string;
  disabled?: boolean;
  onChange: (value: T) => void;
  options: readonly OverlaySelectOption<T>[];
  value: T;
}) {
  const id = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listboxRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<Array<HTMLDivElement | null>>([]);
  const presentation = useOverlaySelectPresentation();
  const selectedIndex = Math.max(
    0,
    options.findIndex((option) => Object.is(option.value, value)),
  );
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(selectedIndex);
  const selected = options[selectedIndex] ?? options[0];
  const listboxId = `${id}-listbox`;

  const close = useCallback((restoreFocus: boolean) => {
    setOpen(false);
    if (restoreFocus) {
      window.requestAnimationFrame(() =>
        triggerRef.current?.focus({ preventScroll: true }),
      );
    }
  }, []);

  const openMenu = useCallback(
    (index = selectedIndex) => {
      if (disabled || options.length === 0) return;
      const nextIndex = options[index]?.disabled
        ? edgeEnabledIndex(options, "first")
        : index;
      setActiveIndex(nextIndex);
      setOpen(true);
    },
    [disabled, options, selectedIndex],
  );

  const choose = useCallback(
    (index: number) => {
      const option = options[index];
      if (!option || option.disabled) return;
      onChange(option.value);
      close(true);
    },
    [close, onChange, options],
  );

  useEffect(() => {
    if (!open) return;
    const frame = window.requestAnimationFrame(() => {
      listboxRef.current?.focus({ preventScroll: true });
      optionRefs.current[activeIndex]?.scrollIntoView?.({ block: "nearest" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [activeIndex, open]);

  useEffect(() => {
    if (!open) return;
    optionRefs.current[activeIndex]?.scrollIntoView?.({ block: "nearest" });
  }, [activeIndex, open]);

  useEffect(() => {
    if (!open) return;
    const dismissOutside = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) close(false);
    };
    const dismissTransient = () => close(false);
    const dismissWhenHidden = () => {
      if (document.hidden) close(false);
    };
    document.addEventListener("pointerdown", dismissOutside, true);
    document.addEventListener("fullscreenchange", dismissTransient);
    document.addEventListener("visibilitychange", dismissWhenHidden);
    window.addEventListener("resize", dismissTransient);
    window.addEventListener("orientationchange", dismissTransient);
    window.addEventListener("mt:deviceorientation", dismissTransient);
    return () => {
      document.removeEventListener("pointerdown", dismissOutside, true);
      document.removeEventListener("fullscreenchange", dismissTransient);
      document.removeEventListener("visibilitychange", dismissWhenHidden);
      window.removeEventListener("resize", dismissTransient);
      window.removeEventListener("orientationchange", dismissTransient);
      window.removeEventListener("mt:deviceorientation", dismissTransient);
    };
  }, [close, open]);

  const onTriggerKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      openMenu(selectedIndex);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      openMenu(edgeEnabledIndex(options, "last"));
    } else if (event.key === "Home") {
      event.preventDefault();
      openMenu(edgeEnabledIndex(options, "first"));
    } else if (event.key === "End") {
      event.preventDefault();
      openMenu(edgeEnabledIndex(options, "last"));
    } else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (open) close(true);
      else openMenu();
    } else if (event.key === "Escape" && open) {
      event.preventDefault();
      close(true);
    }
  };

  const onListboxKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((current) =>
        enabledIndex(options, current, event.key === "ArrowDown" ? 1 : -1),
      );
    } else if (event.key === "Home" || event.key === "End") {
      event.preventDefault();
      setActiveIndex(
        edgeEnabledIndex(options, event.key === "Home" ? "first" : "last"),
      );
    } else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      choose(activeIndex);
    } else if (event.key === "Escape") {
      event.preventDefault();
      close(true);
    } else if (event.key === "Tab") {
      // The open listbox is the sole tab stop in its transient surface.
      event.preventDefault();
      listboxRef.current?.focus({ preventScroll: true });
    }
  };

  const stopFocusLoss = (event: ReactPointerEvent) => {
    event.preventDefault();
  };

  return (
    <div
      className={`overlay-select ${className}`.trim()}
      data-open={open ? "true" : "false"}
      data-presentation={presentation}
      ref={rootRef}
    >
      <button
        aria-controls={listboxId}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={`${ariaLabel}, ${selected?.label ?? ""}`}
        className="overlay-select__trigger"
        disabled={disabled}
        onClick={() => (open ? close(true) : openMenu())}
        onKeyDown={onTriggerKeyDown}
        ref={triggerRef}
        type="button"
      >
        <span className="overlay-select__value">{selected?.label}</span>
        <ChevronDown
          aria-hidden
          className="overlay-select__chevron"
          size={18}
        />
      </button>
      {open ? (
        <div className="overlay-select__layer" data-presentation={presentation}>
          <div
            aria-hidden
            className="overlay-select__backdrop"
            onPointerDown={() => close(false)}
          />
          <div className="overlay-select__surface">
            <header className="overlay-select__header">
              <strong>{ariaLabel}</strong>
              <button
                aria-label={`Close ${ariaLabel}`}
                className="overlay-select__close"
                onClick={() => close(true)}
                type="button"
              >
                <X aria-hidden size={20} />
              </button>
            </header>
            <div
              aria-activedescendant={`${id}-option-${activeIndex}`}
              aria-label={ariaLabel}
              className="overlay-select__listbox"
              id={listboxId}
              onKeyDown={onListboxKeyDown}
              ref={listboxRef}
              role="listbox"
              tabIndex={0}
            >
              {options.map((option, index) => (
                <div
                  aria-disabled={option.disabled || undefined}
                  aria-selected={Object.is(option.value, value)}
                  className="overlay-select__option"
                  data-active={activeIndex === index ? "true" : "false"}
                  data-value={String(option.value)}
                  id={`${id}-option-${index}`}
                  key={String(option.value)}
                  onClick={() => choose(index)}
                  onPointerDown={stopFocusLoss}
                  onPointerMove={() => {
                    if (!option.disabled) setActiveIndex(index);
                  }}
                  ref={(element) => {
                    optionRefs.current[index] = element;
                  }}
                  role="option"
                >
                  <span>{option.label}</span>
                  {Object.is(option.value, value) ? (
                    <Check aria-hidden size={18} />
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
